import api from "./api";
import type {
  EaComment,
  EaCompanySettings,
  EaCompanySettingsUpdate,
  EaDelegation,
  EaDelegationCreate,
  EaDocument,
  EaDocumentCreate,
  EaDocumentUpdate,
  EaExpenseStats,
  EaHistory,
  EaInboxCounts,
  EaInboxPage,
  EaInboxParams,
  EaLineRule,
  EaLineRuleIn,
  EaRuleTargets,
  EaStepIn,
  EaSuggestLineResponse,
  EaTypeCode,
  EaTypeInfo,
  EaTypeSettings,
  EaTypeSettingsUpdate,
  EaUserBrief,
} from "@/types/ea";

const clean = <T extends Record<string, unknown>>(params: T) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""));

export const eaService = {
  // ----- 유형·결재선 -----
  async getTypes(includeSchema = false): Promise<EaTypeInfo[]> {
    const { data } = await api.get<EaTypeInfo[]>("/ea/types", { params: { include_schema: includeSchema } });
    return data;
  },
  async suggestLine(typeCode: EaTypeCode, payload?: Record<string, unknown>): Promise<EaSuggestLineResponse> {
    const { data } = await api.post<EaSuggestLineResponse>(`/ea/types/${typeCode}/suggest-line`, { payload: payload ?? null });
    return data;
  },
  async getApproverCandidates(q?: string): Promise<EaUserBrief[]> {
    const { data } = await api.get<EaUserBrief[]>("/ea/approver-candidates", { params: clean({ q, limit: 200 }) });
    return data;
  },

  // ----- 문서 -----
  async createDocument(body: EaDocumentCreate): Promise<EaDocument> {
    const { data } = await api.post<EaDocument>("/ea/documents", body);
    return data;
  },
  async getDocument(id: number): Promise<EaDocument> {
    const { data } = await api.get<EaDocument>(`/ea/documents/${id}`);
    return data;
  },
  async updateDocument(id: number, body: EaDocumentUpdate): Promise<EaDocument> {
    const { data } = await api.patch<EaDocument>(`/ea/documents/${id}`, body);
    return data;
  },
  async deleteDocument(id: number): Promise<void> {
    await api.delete(`/ea/documents/${id}`);
  },
  async submit(id: number, steps?: EaStepIn[] | null): Promise<EaDocument> {
    const { data } = await api.post<EaDocument>(`/ea/documents/${id}/submit`, steps ? { steps } : {});
    return data;
  },
  async approve(id: number, comment?: string): Promise<EaDocument> {
    const { data } = await api.post<EaDocument>(`/ea/documents/${id}/approve`, { comment: comment || null });
    return data;
  },
  async reject(id: number, reason: string): Promise<EaDocument> {
    const { data } = await api.post<EaDocument>(`/ea/documents/${id}/reject`, { reason });
    return data;
  },
  async returnDocument(id: number, reason: string): Promise<EaDocument> {
    const { data } = await api.post<EaDocument>(`/ea/documents/${id}/return`, { reason });
    return data;
  },
  async withdraw(id: number, reason?: string): Promise<EaDocument> {
    const { data } = await api.post<EaDocument>(`/ea/documents/${id}/withdraw`, { reason: reason || null });
    return data;
  },
  async redraft(id: number): Promise<EaDocument> {
    const { data } = await api.post<EaDocument>(`/ea/documents/${id}/redraft`);
    return data;
  },
  async process(id: number, result: "completed" | "rejected", note?: string): Promise<EaDocument> {
    const { data } = await api.post<EaDocument>(`/ea/documents/${id}/process`, { result, note: note || null });
    return data;
  },
  async reassign(id: number, stepId: number, newAssigneeId: string, reason?: string): Promise<EaDocument> {
    const { data } = await api.post<EaDocument>(`/ea/documents/${id}/reassign`, {
      step_id: stepId, new_assignee_id: newAssigneeId, reason: reason || null,
    });
    return data;
  },
  async resolveLegacy(source: string, id: number): Promise<number | null> {
    const { data } = await api.get<{ document_id: number | null }>("/ea/documents/resolve-legacy", { params: { source, id } });
    return data.document_id;
  },

  // ----- 결재함 -----
  async getInbox(params: EaInboxParams): Promise<EaInboxPage> {
    const { data } = await api.get<EaInboxPage>("/ea/inbox", { params: clean({ page: 1, page_size: 20, ...params }) });
    return data;
  },
  async getInboxCounts(): Promise<EaInboxCounts> {
    const { data } = await api.get<EaInboxCounts>("/ea/inbox/counts");
    return data;
  },
  async getProcessingAccess(): Promise<boolean> {
    const { data } = await api.get<{ processing_access: boolean }>("/ea/me/processing-access");
    return data.processing_access;
  },

  // ----- 첨부 -----
  async uploadAttachment(id: number, file: File, kind: "general" | "receipt" = "general") {
    const form = new FormData();
    form.append("file", file);
    form.append("kind", kind);
    const { data } = await api.post(`/ea/documents/${id}/attachments`, form, {
      headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
    });
    return data;
  },
  async deleteAttachment(id: number, attachmentId: number): Promise<void> {
    await api.delete(`/ea/documents/${id}/attachments/${attachmentId}`);
  },
  async getAttachmentDownloadUrl(id: number, attachmentId: number): Promise<{ url: string; file_name: string }> {
    const { data } = await api.get(`/ea/documents/${id}/attachments/${attachmentId}/download`);
    return data;
  },
  attachmentDownloadPath(id: number, attachmentId: number): string {
    return `/api/v1/ea/documents/${id}/attachments/${attachmentId}/download`;
  },

  // ----- 댓글·이력·PDF -----
  async getComments(id: number): Promise<EaComment[]> {
    const { data } = await api.get<EaComment[]>(`/ea/documents/${id}/comments`);
    return data;
  },
  async addComment(id: number, content: string): Promise<EaComment> {
    const { data } = await api.post<EaComment>(`/ea/documents/${id}/comments`, { content });
    return data;
  },
  async deleteComment(id: number, commentId: number): Promise<void> {
    await api.delete(`/ea/documents/${id}/comments/${commentId}`);
  },
  async getHistory(id: number): Promise<EaHistory[]> {
    const { data } = await api.get<EaHistory[]>(`/ea/documents/${id}/history`);
    return data;
  },
  async downloadPdf(id: number): Promise<Blob> {
    const { data } = await api.get(`/ea/documents/${id}/pdf`, { responseType: "blob", timeout: 60000 });
    return data as Blob;
  },

  // ----- 대결 -----
  async getMyDelegations(): Promise<EaDelegation[]> {
    const { data } = await api.get<EaDelegation[]>("/ea/delegations/mine");
    return data;
  },
  async createDelegation(body: EaDelegationCreate): Promise<EaDelegation> {
    const { data } = await api.post<EaDelegation>("/ea/delegations", body);
    return data;
  },
  async deleteDelegation(id: number): Promise<void> {
    await api.delete(`/ea/delegations/${id}`);
  },

  // ----- 관리자 -----
  async getSettings(): Promise<EaCompanySettings> {
    const { data } = await api.get<EaCompanySettings>("/ea/admin/settings");
    return data;
  },
  async updateSettings(body: EaCompanySettingsUpdate): Promise<EaCompanySettings> {
    const { data } = await api.put<EaCompanySettings>("/ea/admin/settings", body);
    return data;
  },
  async getTypeSettings(): Promise<EaTypeSettings[]> {
    const { data } = await api.get<EaTypeSettings[]>("/ea/admin/types");
    return data;
  },
  async updateTypeSettings(typeCode: EaTypeCode, body: EaTypeSettingsUpdate): Promise<EaTypeSettings> {
    const { data } = await api.put<EaTypeSettings>(`/ea/admin/types/${typeCode}`, body);
    return data;
  },
  async seedDefaultRule(typeCode: EaTypeCode): Promise<EaLineRule> {
    const res = await api.post<EaLineRule>(`/ea/admin/types/${typeCode}/rules/seed-default`);
    return res.data;
  },
  async getRules(typeCode: EaTypeCode): Promise<EaLineRule[]> {
    const { data } = await api.get<EaLineRule[]>(`/ea/admin/types/${typeCode}/rules`);
    return data;
  },
  async createRule(typeCode: EaTypeCode, body: EaLineRuleIn): Promise<EaLineRule> {
    const { data } = await api.post<EaLineRule>(`/ea/admin/types/${typeCode}/rules`, body);
    return data;
  },
  async updateRule(typeCode: EaTypeCode, ruleId: number, body: EaLineRuleIn): Promise<EaLineRule> {
    const { data } = await api.put<EaLineRule>(`/ea/admin/types/${typeCode}/rules/${ruleId}`, body);
    return data;
  },
  async deleteRule(typeCode: EaTypeCode, ruleId: number): Promise<void> {
    await api.delete(`/ea/admin/types/${typeCode}/rules/${ruleId}`);
  },
  async previewRules(typeCode: EaTypeCode, drafterId?: string, payload?: Record<string, unknown>): Promise<EaSuggestLineResponse> {
    const { data } = await api.post<EaSuggestLineResponse>(`/ea/admin/types/${typeCode}/rules/preview`, {
      drafter_id: drafterId || null, payload: payload || null,
    });
    return data;
  },
  async getRuleTargets(): Promise<EaRuleTargets> {
    const { data } = await api.get<EaRuleTargets>("/ea/admin/targets");
    return data;
  },

  // ----- 통계 -----
  async getExpenseStats(year: number, typeCode?: EaTypeCode): Promise<EaExpenseStats> {
    const { data } = await api.get<EaExpenseStats>("/ea/stats/expense", { params: clean({ year, type_code: typeCode }) });
    return data;
  },
  async downloadReceiptsPdf(year: number, month: number): Promise<Blob> {
    const { data } = await api.get("/ea/stats/expense/receipts.pdf", { params: { year, month }, responseType: "blob", timeout: 120000 });
    return data as Blob;
  },
};

export default eaService;
